import { readWithRecovery } from '../utils/loadWithRecovery';

const LANGUAGES = new Set(['en', 'ru', 'zh', 'es', 'pt', 'ja', 'tr', 'ko', 'fr', 'de', 'it', 'hi']);
const CJK_CHARACTER = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;
export const SEARCH_MAX_LENGTH = 120;

export function normalizeSearchLanguage(language) {
  const code = String(language || '').toLowerCase().split(/[-_]/)[0];
  return LANGUAGES.has(code) ? code : 'en';
}

export function normalizeSearchQuery(query, kind = 'stories') {
  const normalized = String(query || '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
  return kind === 'users' ? normalized.replace(/^@/, '').trim() : normalized;
}

export function isSearchableQuery(query, kind = 'stories') {
  const text = normalizeSearchQuery(query, kind);
  const points = Array.from(text);
  const invalidPoint = (point) => {
    const code = point.codePointAt(0);
    return code <= 8 || code === 11 || code === 12 || (code >= 14 && code <= 31)
      || code === 127 || (code >= 0xd800 && code <= 0xdfff);
  };
  if (points.length > SEARCH_MAX_LENGTH || points.some(invalidPoint)) return false;
  return points.length > 1 || (points.length === 1 && CJK_CHARACTER.test(text));
}

function searchError(code, status = 0, retryAfter = null) {
  const error = new Error(code);
  error.code = code;
  error.status = status;
  error.retryAfter = retryAfter;
  return error;
}

// Public search deliberately bypasses the session-aware request helper: it must
// not create a fingerprint, send tokens, or interrupt the user's current feed.
export function searchPublic(options) {
  return readWithRecovery(({ signal }) => searchOnce({ ...options, signal }), { signal: options.signal });
}

async function searchOnce({ kind = 'stories', query, language = 'en', cursor, signal, limit = 20 }) {
  if (!['stories', 'users'].includes(kind)) throw searchError('SEARCH_INVALID_QUERY', 400);
  if (!isSearchableQuery(query, kind)) return { items: [], nextCursor: null, hasMore: false };
  const prefix = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  // The API removes one leading @ for people. Preserve it on the wire so a
  // literal second @ is not accidentally stripped once on each side.
  const params = new URLSearchParams({ q: normalizeSearchQuery(query), limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (signal?.aborted) cancel();
  else signal?.addEventListener('abort', cancel, { once: true });
  let timedOut = false;
  const timeout = setTimeout(() => { timedOut = true; cancel(); }, 10000);
  try {
    const response = await fetch(`${prefix}/search/${kind}?${params}`, {
      method: 'GET', headers: { lng: normalizeSearchLanguage(language) },
      credentials: 'omit', cache: 'no-store', signal: controller.signal,
    });
    let data;
    try { data = await response.json(); } catch { throw searchError('SEARCH_UNAVAILABLE', response.status); }
    if (!response.ok) {
      const retryAfter = Number(response.headers?.get('Retry-After')) || null;
      throw searchError(data?.error?.code || 'SEARCH_UNAVAILABLE', response.status, retryAfter);
    }
    const validItem = (item) => item && Number.isInteger(item.id) && item.id > 0
      && (kind === 'stories' ? typeof item.title === 'string' : typeof item.user_name === 'string');
    if (!Array.isArray(data?.items) || !data.items.every(validItem) || typeof data.hasMore !== 'boolean'
      || !(data.nextCursor === null || typeof data.nextCursor === 'string')
      || (data.hasMore && !data.nextCursor)) throw searchError('SEARCH_UNAVAILABLE', response.status);
    return data;
  } catch (error) {
    if (timedOut) throw searchError('SEARCH_TIMEOUT');
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', cancel);
  }
}
